import { useCallback, useEffect, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import {
    useDeleteWorkTemplate,
    usePatchWorkTemplate,
    usePostWorkTemplate,
    useWorkTemplatesQuery,
    WorkTemplateDto,
} from '@/features/workTemplate';
import { WorkTemplate } from '../types/workingHours';

const LEGACY_KEY_PREFIX = 'work-templates-';
const MIGRATION_FLAG_PREFIX = 'work-templates-migrated-';

/** 'HH:mm' → ISO (오늘 날짜 기준) */
const hhmmToIso = (hhmm: string | null | undefined): string | undefined => {
    if (!hhmm) return undefined;
    const [hStr, mStr] = hhmm.split(':');
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return undefined;
    return dayjs().hour(h).minute(m).second(0).millisecond(0).toISOString();
};

/** ISO → 'HH:mm' */
const isoToHhmm = (iso: string | null | undefined): string | null => {
    if (!iso) return null;
    const d = dayjs(iso);
    if (!d.isValid()) return null;
    return d.format('HH:mm');
};

/**
 * DTO(서버) → UI 타입 변환
 * - UI 타입의 시간은 ISO string (TimePickerInput2 호환)
 * - DTO의 HH:mm → ISO로 변환
 */
const dtoToUi = (dto: WorkTemplateDto): WorkTemplate => ({
    id: dto.id,
    name: dto.name,
    type: dto.type,
    startTime: hhmmToIso(dto.startTime),
    endTime: hhmmToIso(dto.endTime),
    memo: dto.memo ?? undefined,
});

/**
 * 센터 근무 템플릿 CRUD 훅.
 *
 * 서버 API 기반. 소비처 UI는 `{ templates, createTemplate, updateTemplate, deleteTemplate }`
 * 인터페이스만 바라보므로, 내부 구현 교체에도 영향을 받지 않는다.
 */
export const useWorkTemplates = (centerId: string) => {
    const { data } = useWorkTemplatesQuery({ centerId, enabled: !!centerId });
    const postMutation = usePostWorkTemplate();
    const patchMutation = usePatchWorkTemplate();
    const deleteMutation = useDeleteWorkTemplate();

    const templates = useMemo<WorkTemplate[]>(
        () => (data?.workTemplates ?? []).map(dtoToUi),
        [data],
    );

    // --- 센터별 1회 마이그레이션: localStorage → 서버 ---
    const migrationAttempted = useRef<Record<string, boolean>>({});
    useEffect(() => {
        if (!centerId) return;
        if (migrationAttempted.current[centerId]) return;
        if (!data) return; // GET 응답 도착 전에는 보류
        migrationAttempted.current[centerId] = true;

        try {
            // 이미 마이그레이션했거나, 서버에 한 개라도 있으면 스킵 (덮어쓰기 방지)
            if (localStorage.getItem(MIGRATION_FLAG_PREFIX + centerId)) return;
            if ((data.workTemplates?.length ?? 0) > 0) {
                localStorage.setItem(MIGRATION_FLAG_PREFIX + centerId, '1');
                localStorage.removeItem(LEGACY_KEY_PREFIX + centerId);
                return;
            }

            const raw = localStorage.getItem(LEGACY_KEY_PREFIX + centerId);
            if (!raw) {
                localStorage.setItem(MIGRATION_FLAG_PREFIX + centerId, '1');
                return;
            }

            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed) || parsed.length === 0) {
                localStorage.removeItem(LEGACY_KEY_PREFIX + centerId);
                localStorage.setItem(MIGRATION_FLAG_PREFIX + centerId, '1');
                return;
            }

            // 순차적 POST (작은 개수이므로 직렬 처리)
            // localStorage에 저장된 구 형식(ISO string) → HH:mm으로 정규화
            const toHhmm = (raw: unknown): string | null => {
                if (typeof raw !== 'string' || !raw) return null;
                // 이미 HH:mm 포맷인 경우 그대로
                if (/^\d{2}:\d{2}$/.test(raw)) return raw;
                // ISO로 해석 시도
                return isoToHhmm(raw);
            };
            (async () => {
                for (const item of parsed) {
                    if (!item || typeof item.name !== 'string') continue;
                    const type: 'include' | 'exclude' = item.type === 'exclude' ? 'exclude' : 'include';
                    try {
                        await postMutation.mutateAsync({
                            centerId,
                            body: {
                                name: item.name,
                                type,
                                startTime: type === 'include' ? toHhmm(item.startTime) : null,
                                endTime: type === 'include' ? toHhmm(item.endTime) : null,
                                memo: typeof item.memo === 'string' ? item.memo : null,
                            },
                        });
                    } catch (e) {
                        // 개별 실패는 조용히 무시 — 사용자가 재등록 가능
                        console.warn('[workTemplates] 마이그레이션 일부 실패:', e);
                    }
                }
                localStorage.removeItem(LEGACY_KEY_PREFIX + centerId);
                localStorage.setItem(MIGRATION_FLAG_PREFIX + centerId, '1');
            })();
        } catch (e) {
            // 전체 실패: 조용히 무시
            console.warn('[workTemplates] 마이그레이션 실패:', e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [centerId, data]);

    // --- CRUD 래퍼 ---
    const createTemplate = useCallback(
        (input: Omit<WorkTemplate, 'id'>) => {
            if (!centerId) return;
            postMutation.mutate({
                centerId,
                body: {
                    name: input.name,
                    type: input.type,
                    startTime: input.type === 'include' ? isoToHhmm(input.startTime) : null,
                    endTime: input.type === 'include' ? isoToHhmm(input.endTime) : null,
                    memo: input.memo ?? null,
                },
            });
        },
        [centerId, postMutation],
    );

    const updateTemplate = useCallback(
        (id: string, patch: Partial<Omit<WorkTemplate, 'id'>>) => {
            if (!centerId) return;
            patchMutation.mutate({
                centerId,
                templateId: id,
                body: {
                    ...(patch.name !== undefined ? { name: patch.name } : {}),
                    ...(patch.type !== undefined ? { type: patch.type } : {}),
                    ...(patch.startTime !== undefined ? { startTime: isoToHhmm(patch.startTime) } : {}),
                    ...(patch.endTime !== undefined ? { endTime: isoToHhmm(patch.endTime) } : {}),
                    ...(patch.memo !== undefined ? { memo: patch.memo ?? null } : {}),
                },
            });
        },
        [centerId, patchMutation],
    );

    const deleteTemplate = useCallback(
        (id: string) => {
            if (!centerId) return;
            deleteMutation.mutate({ centerId, templateId: id });
        },
        [centerId, deleteMutation],
    );

    return { templates, createTemplate, updateTemplate, deleteTemplate };
};
